import React, { useEffect, useRef, useState } from "react";
import { Location } from "history";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LimitComponentVisibility } from "go-core/components/LimitComponentVisibility";
import { SaveAndAddButtons, TranslationType } from "go-core/components/SaveAndAddButtons";
import useSubscriptionBasedRestriction from "go-core/components/SubscriptionBasedRestriction/hooks/useSubscriptionBasedRestriction";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { hasPermission, selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { SaveAndAddModal } from "../../../../../../../../../../components/Common/SaveAndAddItem";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { EmployeeApi, SaveAndAddActionType } from "../../../../../../../../../../services/Api/Organization/types";
import { employeeTipsEnabled } from "../../../../../../../../../../utils/envutil";
import { handleAddEmployeeBasedOnSubscription } from "../../../utils";
import EmployeeWorkplacesForm from "../EmployeeWorkplacesForm";
import EmployeeBasicInfoForm from "./EmployeeBasicInfoForm";
import EmployeeContactForm from "./EmployeeContactForm";
import EmployeeInformationForm from "./EmployeeInformationForm";
import EmployeeTipsForm from "./EmployeeTipsForm";

interface Props {
	employee?: EmployeeApi;
	customFieldsConfig: CustomFieldTemplateApi[];
}

const formEmptyValues = {
	name: "",
	tax_id_no: "",
	card_code: "",
	card_number: "",
	address_street: "",
	address_build_nr: "",
	address_flat_nr: "",
	address_zip_code: "",
	address_city: "",
	address_country: "",
	contact_email: "",
	contact_other: "",
	nin_no: "",
	id_card_number: "",
	id_card_issuing_authority: "",
	avatar: undefined,
	avatar_link: undefined,
	birthday: undefined,
	card_expires_at: undefined,
	client_group_id: undefined,
	color: undefined,
	contact_phone_number: undefined,
	id_card_issued_at: undefined,
	label: undefined,
	custom_fields: undefined,
};

const EmployeeForm = (props: Props): JSX.Element => {
	const [newAvatar, setNewAvatar] = useState<string | undefined>(props.employee?.avatar_link?.default_link);
	const [loading, setLoading] = useState(false);
	const [saveAndAddLoading, setSaveAndAddLoading] = useState(false);
	const [saveAndAddModalActive, setSaveAndAddModalActive] = useState<EmployeeApi | undefined>(undefined);
	const saveAndAddMode = useRef(false);
	const location: Location<{ accept_tips: boolean }> = useLocation();
	const form = useForm<EmployeeApi>({
		criteriaMode: "all",
		defaultValues: {
			...props.employee,
			custom_fields: mapCustomFieldsToForm(props.customFieldsConfig, props.employee?.custom_fields || []),
		},
	});
	const organization = useSelector(selectOrganization);
	const history = useHistory();
	const { t } = useTranslation();
	const hasVenueEmployeeHourlyRateAccess = useSelector(hasPermission("VENUE_EMPLOYEE_HOURLY_RATE_ACCESS"));
	const { addFlash, addSuccessFlash } = useFlash();
	const { handleSubmit, reset, setError, formState, getValues, setValue } = form;
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const isEdit = location.pathname.includes("/edit");
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const {
		handleShowSubscriptionBasedRestrictionModal,
		renderSubscriptionBasedRestrictionModal,
		handleSetSubscriptionBasedRestrictionModalConfig,
	} = useSubscriptionBasedRestriction();

	useEffect(() => {
		if (props.employee) {
			handleChangeTabTitle(`${props.employee?.name || ""} | ${t("modules.employee.header.title")}`);
		}
	}, [props.employee?.name]);

	useEffect(() => {
		if (location.state?.accept_tips !== undefined) {
			setValue("accept_tips", location.state.accept_tips, { shouldDirty: true });
		}
	}, [location.state?.accept_tips]);

	const onSubmit = async (e?: React.BaseSyntheticEvent<Record<string, any>, any, any> | undefined) => {
		e?.preventDefault();
		await handleAddEmployeeBasedOnSubscription(
			organization,
			handleSetSubscriptionBasedRestrictionModalConfig,
			t,
			handleShowSubscriptionBasedRestrictionModal,
			() => onSubmitCallback(e),
			undefined,
			isEdit
		);
	};

	const onSubmitCallback = handleSubmit(async (data: EmployeeApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		saveAndAddMode.current ? setSaveAndAddLoading(true) : setLoading(true);

		if (props.employee?.id) data.id = props.employee.id;

		if (getValues("avatar.image_data") || !newAvatar) {
			data.avatar_link = undefined;
		}
		if (!getValues("color")) {
			data.color = undefined;
		}

		if (!employeeTipsEnabled()) {
			delete data.accept_tips;
			delete data.bank_account_number;
		}

		try {
			if (data.id && isEdit) {
				const params: Record<string, any> = {};
				params.include = "role,role.permissions,workplace,workplaces,workplaces.workplace,custom_fields";
				const res = await apiOrganization.updateEmployee(data, params);
				if (props.employee?.avatar_link) props.employee.avatar_link = res.avatar_link;
				if (saveAndAddMode.current) {
					history.push(`/${organization.id}/employees/new`);
				}
				reset({
					...res,
					code: res.code ? res.code : "",
					contact_phone_number: res?.contact_phone_number ? res.contact_phone_number : "",
					bank_account_number: res?.bank_account_number ?? "",
					venue_role: res.venue_role ?? null,
					venue_role_id: res.venue_role_id ?? null,
					workplace: res.workplace ?? null,
					workplace_id: res.workplace_id ?? null,
					custom_fields: mapCustomFieldsToForm(props.customFieldsConfig, res.custom_fields),
				});
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
				handleChangeTabTitle(`${res.name || ""} | ${t("modules.employee.header.title")}`);
			} else {
				const res = await apiOrganization.createEmployee(data);
				if (saveAndAddMode.current) {
					setSaveAndAddModalActive(res);
				} else history.push(`/${organization.id}/employees/${res.id}/edit`);
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			}
		} catch (e) {
			handleError.form(e, setError, addFlash);
		} finally {
			setLoading(false);
			setSaveAndAddLoading(false);
			saveAndAddMode.current = false;
		}
	});

	const onHideSaveAndAddModal = () => {
		saveAndAddMode.current = false;
		setSaveAndAddLoading(false);
		setSaveAndAddModalActive(undefined);
	};

	const saveAndAddItemHandler = async () => {
		saveAndAddMode.current = true;
		await onSubmit();
	};

	const onSaveAndAdd = async (action: SaveAndAddActionType, itemId?: string) => {
		if (action === SaveAndAddActionType.ADD) {
			reset(formEmptyValues);
			setValue("hourly_rate.amount", null);
		} else {
			if (!itemId) return;
			try {
				const params: Record<string, any> = {};
				params.include = "role,workplace,workplaces,custom_fields,hourly_rate,avatar";
				const res = await apiOrganization.getEmployee(Number(itemId), params);
				reset({ ...formEmptyValues, avatar_link: res.avatar_link });
				reset({
					...props.employee,
					...res,
					name: `${t("common.word.copy")} ${res.name}`,
					custom_fields: mapCustomFieldsToForm(props.customFieldsConfig, res.custom_fields),
					code: "",
					card_code: "",
					contact_email: "",
					contact_phone_number: "",
					address_street: "",
					address_build_nr: "",
					address_flat_nr: "",
					address_zip_code: "",
					address_city: "",
					address_country: "",
					contact_other: "",
				});
			} catch (err) {
				handleError.alert(err, addFlash);
			}
		}
		setSaveAndAddLoading(false);
		setSaveAndAddModalActive(undefined);
	};

	return (
		<>
			{renderSubscriptionBasedRestrictionModal()}
			<FormDirty
				formState={formState}
				key="employee-form"
				loading={loading}
				noValidate
				onSubmit={onSubmit}
				buttonSubmitCustom={
					<SaveAndAddButtons
						saveLoading={loading}
						saveAndAddLoading={saveAndAddLoading}
						onSaveAndAddClick={saveAndAddItemHandler}
						translationType={TranslationType.MALE}
					/>
				}
			>
				<EmployeeBasicInfoForm
					form={form}
					setNewAvatar={setNewAvatar}
					newAvatar={newAvatar}
					employee={props.employee}
				/>
				{employeeTipsEnabled() && <EmployeeTipsForm form={form} />}
				<EmployeeInformationForm form={form} />
				<EmployeeContactForm form={form} />
				{props.customFieldsConfig.length > 0 && (
					<fieldset className="form-group no-gutters">
						<h5>{t("lib:common.word.advanced")}</h5>
						<CustomFieldsForm
							form={form}
							customFieldsConfig={props.customFieldsConfig}
							customFields={props.employee?.custom_fields || []}
							setErrors={setErrors}
						/>
					</fieldset>
				)}
				{hasVenueEmployeeHourlyRateAccess && (
					<fieldset className={"form-group"}>
						<LimitComponentVisibility
							defaultVisible={
								typeof props.employee?.workplaces?.length === "number" &&
								props.employee?.workplaces?.length > 0
							}
							id={"emp_workplaces"}
							handleChange={(val) => val === false && setValue("workplaces", [], { shouldDirty: true })}
							legendText={t("modules.employee.field.workplaces_legend.title")}
							helpText={t("modules.employee.field.workplaces_legend.help_text.description")}
							unlimitedContent={<EmployeeWorkplacesForm form={form} />}
						/>
					</fieldset>
				)}
				{saveAndAddModalActive && (
					<SaveAndAddModal
						onHide={onHideSaveAndAddModal}
						onSaveAndAdd={onSaveAndAdd}
						onFetchItems={apiOrganization.getEmployeesSearchSelect}
						itemValue={saveAndAddModalActive}
						translations={{
							copyItemLabel: t("modules.employee.field.copy_employee.title"),
							copyItemDescription: t("modules.employee.field.copy_employee.description.title"),
							newItemLabel: t("modules.employee.field.new_employee.title"),
							newItemDescription: t("modules.employee.field.new_employee.description.title"),
							chooseItem: t("modules.employee.action.choose_employee.title"),
							copyItemHeader: t("modules.employee.action.create_employee.title"),
						}}
					/>
				)}
			</FormDirty>
		</>
	);
};
export default EmployeeForm;
